import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

type DropdownItem = {
  label: string;
  url: string;
};

const AuthLayoutFrameFooterResources: React.FC = () => {
  const { t } = useTranslation();
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

  useEffect(() => {
    if (!isDropdownVisible) {
      return;
    }

    const handleClickAnywhere = () => {
      setIsDropdownVisible(false);
    };

    document.addEventListener('click', handleClickAnywhere);
    return () => {
      document.removeEventListener('click', handleClickAnywhere);
    };
  }, [isDropdownVisible]);

  const toggleDropdown = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setIsDropdownVisible(!isDropdownVisible);
  };

  const items: DropdownItem[] = [
    {
      label: t('header.epdLibrary'),
      url: 'https://www.environdec.com/library',
    },
    {
      label: t('header.pcrLibrary'),
      url: 'https://www.environdec.com/pcr-library',
    },
    {
      label: t('header.verification'),
      url: 'https://www.environdec.com/services/third-party-verification',
    },
    {
      label: t('header.pricing'),
      url: 'https://www.environdec.com/pricing',
    },
  ];

  return (
    <Wrapper>
      <Button onClick={toggleDropdown} $isOpen={isDropdownVisible}>
        Resources
      </Button>

      {isDropdownVisible && (
        <DropdownContainer>
          {items.map((item) => (
            <DropdownLink key={item.label} href={item.url} target="_blank" rel="noopener noreferrer">
              {item.label}
            </DropdownLink>
          ))}
        </DropdownContainer>
      )}
    </Wrapper>
  );
};

export default AuthLayoutFrameFooterResources;

const Wrapper = styled.span`
  position: relative;
`;

const Button = styled.button<{ $isOpen: boolean }>`
  all: unset;
  display: inline;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: auto;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlack};
  }

  ${(props) =>
    props.$isOpen &&
    css`
      color: ${props.theme.colors.primaryOrange};
    `}
`;

const DropdownContainer = styled.div`
  min-width: 174px;
  position: absolute;
  left: -2px;
  bottom: calc(100% + 8px);
  border-radius: 4px;
  background-color: #ffffff;
  box-shadow: 0 8px 20px 0 rgba(0, 0, 0, 0.15);

  transform-origin: bottom;
  transform: scaleY(100%);
  opacity: 1;
  transition: transform 100ms ease-out, opacity 300ms;
  @starting-style {
    transform: scaleY(0);
    opacity: 0;
  }
`;

const DropdownLink = styled.a`
  display: block;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 16px;
  padding-right: 8px;
  color: ${(props) => props.theme.colors.primaryBlack};
  font-size: 14px;
  line-height: 1;
  text-decoration: none;

  &:hover {
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;
